import React from 'react';
import { Check, Star, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CreditPackageVariant, CreditPackage } from '@aaas/shared';

interface PricingCardProps {
  variant: CreditPackageVariant;
  package?: CreditPackage;
  isPopular?: boolean;
  onSelect: (variantId: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  variant,
  package: creditPackage,
  isPopular = false,
  onSelect,
  isLoading = false,
  disabled = false,
}) => {
  const { t } = useTranslation('portal');
  // Since pricing info is no longer in variant, show placeholder or get from external source
  // TODO: Implement proper pricing display when pricing data is available
  const credits = creditPackage?.credits_amount || 0;
  const formattedPrice = '$0.00'; // Placeholder

  const handleSelect = () => {
    if (!disabled && !isLoading) {
      onSelect(variant.id);
    }
  };

  return (
    <div
      className={`relative rounded-lg border-2 transition-all duration-200 ${
        isPopular
          ? 'border-blue-500 shadow-lg scale-105'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="flex items-center space-x-1 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
            <Star className="w-4 h-4 fill-current" />
            <span>{t('pricingCard.mostPopular')}</span>
          </div>
        </div>
      )}

      <div className={`p-6 ${isPopular ? 'pt-8' : ''}`}>
        {/* Header */}
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {creditPackage?.name || t('pricingCard.heading')}
          </h3>
          <div className="text-3xl font-bold text-gray-900 mb-1">{formattedPrice}</div>
          <div className="text-sm text-gray-500">
            {t('pricingCard.forCredits', { credits: credits.toLocaleString() })}
          </div>
        </div>

        {/* Value Indicator */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            <Check className="w-4 h-4 mr-1" />
            {t('pricingCard.bestValue')}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>{t('pricingCard.featureShared')}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>{t('pricingCard.featureNeverExpires')}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>{t('pricingCard.featureInstant')}</span>
          </div>
        </div>

        {/* Purchase Button */}
        <button
          onClick={handleSelect}
          disabled={disabled || isLoading}
          className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors ${
            isPopular
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-900 hover:bg-gray-800 text-white'
          } ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              <span>{t('pricingCard.purchaseButton')}</span>
            </>
          )}
        </button>

        {/* Additional Info */}
        <div className="mt-3 text-center text-xs text-gray-500">{t('pricingCard.footer')}</div>
      </div>
    </div>
  );
};
